import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Badge, Box, Column, DropdownMenu, DropdownMenuItem, IconButton, Row, Typography } from 'seagull';
import { Archive, Edit, Email, Kebab, Phone, Trash } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import ClientViewMenuItem from '~/components/ClientViewMenuItem/ClientViewMenuItem';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { AccountingMappingIndication } from '~/integrations/Accounting/AccountingMappingIndication/AccountingMappingIndication';
import { eligibilityTooltipProps, eligibilityTooltipsTextsPath } from '~/pages/ContactsDashboard/ContactsDashboard.utils';
import { hasPortalAccess, portalAccessDisabledTooltip } from '~/pages/ContactsDashboard/ContactsDashboardRow.utils';
import styles from './ContactItem.module.scss';
import { deleteTooltipProps } from './ContactItem.utils';
export const GetContactItemTID = (parentTestId)=>getTestIds(parentTestId);
export function ContactItem(props) {
    var _contactModel_customerId, _contactModel_userId;
    const { contact, contactModel, testId, isOnlyContact, onEdit, onArchive, onDelete } = props;
    const { ContactItemTestIds: TID } = useMemo(()=>GetContactItemTID(testId), [
        testId
    ]);
    const { t: contactsTexts } = useLocaleKeys('pages.clientDetail.summary.contacts');
    const { t: eligibilityTooltipsTexts } = useLocaleKeys(eligibilityTooltipsTextsPath);
    const { t: clientViewTexts } = useLocaleKeys('components.clientView');
    const { ENABLE_KILL_ACCOUNTANT_ACCESS_FE, CRM_DRAFT_CONTACTS } = useFeatureFlags();
    const reviewerOnlyGate = CRM_DRAFT_CONTACTS === true;
    const showActionsMenu = !contactModel.isSample();
    const deleteDisabled = !contactModel.canDelete() || isOnlyContact;
    const deleteTooltip = deleteTooltipProps(contactModel, isOnlyContact, contactsTexts.lastContactDeleteTooltip(), eligibilityTooltipsTexts);
    return /*#__PURE__*/ _jsx(Box, {
        testId: testId,
        className: styles.row,
        children: /*#__PURE__*/ _jsxs(Column, {
            gap: 8,
            children: [
                /*#__PURE__*/ _jsxs(Row, {
                    gap: 8,
                    alignItems: "center",
                    justifyContent: "space-between",
                    children: [
                        /*#__PURE__*/ _jsxs(Row, {
                            gap: 8,
                            alignItems: "center",
                            children: [
                                /*#__PURE__*/ _jsx(Typography, {
                                    weight: "bold",
                                    children: contact.name
                                }),
                                contact.mappings.map((mapping)=>/*#__PURE__*/ _jsx(AccountingMappingIndication, {
                                        accounting: mapping.accounting,
                                        display: mapping.display,
                                        purpose: "customer"
                                    }, mapping.accounting)),
                                !contact.portalAccess && /*#__PURE__*/ _jsx(Badge, {
                                    skin: "default",
                                    rounded: true,
                                    outlined: true,
                                    testId: TID.NoPortalAccess(),
                                    children: contactsTexts.noPortalAccess()
                                })
                            ]
                        }),
                        showActionsMenu && /*#__PURE__*/ _jsxs(DropdownMenu, {
                            gap: 0,
                            target: /*#__PURE__*/ _jsx(IconButton, {
                                bi: {
                                    name: 'client_detail_contact_menu'
                                },
                                testId: TID.Menu(),
                                skin: "transparent",
                                children: /*#__PURE__*/ _jsx(Kebab, {
                                    size: 20
                                })
                            }),
                            children: [
                                /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                    bi: {
                                        name: 'client_detail_contact_edit'
                                    },
                                    testId: TID.MenuEdit(),
                                    iconStart: /*#__PURE__*/ _jsx(Edit, {}),
                                    onClick: onEdit,
                                    children: contactsTexts.menu.edit()
                                }),
                                /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                    bi: {
                                        name: 'client_detail_contact_archive'
                                    },
                                    testId: TID.MenuArchive(),
                                    iconStart: /*#__PURE__*/ _jsx(Archive, {}),
                                    onClick: onArchive,
                                    disabled: !contactModel.canArchive(),
                                    tooltipProps: eligibilityTooltipProps(contactModel.archiveEligibility(), 'archive', eligibilityTooltipsTexts),
                                    children: contactsTexts.menu.archive()
                                }),
                                ENABLE_KILL_ACCOUNTANT_ACCESS_FE && /*#__PURE__*/ _jsx(ClientViewMenuItem, {
                                    clientBusinessId: (_contactModel_customerId = contactModel.customerId()) !== null && _contactModel_customerId !== void 0 ? _contactModel_customerId : null,
                                    viewUserId: (_contactModel_userId = contactModel.userId()) !== null && _contactModel_userId !== void 0 ? _contactModel_userId : undefined,
                                    disabled: !hasPortalAccess(contactModel, reviewerOnlyGate),
                                    disabledTooltip: portalAccessDisabledTooltip({
                                        contact: contactModel,
                                        clientViewTexts,
                                        reviewerOnlyGate
                                    }),
                                    testId: TID.ViewPortal()
                                }),
                                /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                    bi: {
                                        name: 'client_detail_contact_delete'
                                    },
                                    testId: TID.MenuDelete(),
                                    iconStart: /*#__PURE__*/ _jsx(Trash, {}),
                                    skin: "error",
                                    onClick: onDelete,
                                    disabled: deleteDisabled,
                                    tooltipProps: deleteTooltip,
                                    children: contactsTexts.menu.delete()
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs(Row, {
                    gap: 8,
                    alignItems: "center",
                    children: [
                        /*#__PURE__*/ _jsx(Email, {
                            size: 14
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            skin: "secondary",
                            children: contact.email
                        })
                    ]
                }),
                contact.phone !== '' && /*#__PURE__*/ _jsxs(Row, {
                    gap: 8,
                    alignItems: "center",
                    testId: TID.Phone(),
                    children: [
                        /*#__PURE__*/ _jsx(Phone, {
                            size: 14
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            skin: "secondary",
                            children: contact.phone
                        })
                    ]
                })
            ]
        })
    });
}
